import React, { useEffect, useMemo, useRef, useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import { useStacklineTheme, hexToRgba, AppImage } from '@stackline/ui';
import { SlButton, SlButtonVariant } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useDorisData } from 'src/utils/Hooks/useDorisData';
import { useDorisEntityDropdownOptions } from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalTrafficDoubleDropdownTopEntity';
import { DorisDropdownFieldNameEnum } from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import { SearchTextField } from 'src/components/AtlasV2Components/Common/SearchTextField';
import { DynamicDownChevron } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/DynamicDownChevron';
import colors from 'src/utils/colors';
import { titleCase } from 'src/utils/stringFormatting';

const optionTitleTextStyle = {
  fontSize: '10px',
  color: colors.grey,
  textTransform: 'uppercase',
  letterSpacing: '0.96px',
  lineHeight: '16px'
};

const optionMenuItemTextStyle = {
  fontSize: '14px',
  color: colors.darkBlue,
  fontWeight: 400,
  lineHeight: '20px',
  letterSpacing: '0.08px',
  marginLeft: '12px',
  // Ensure long labels truncate with ellipsis within the menu width
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
  minWidth: 0
};

export type SearchableDropdownOption = {
  id: string;
  label: string;
  indexName?: string;
  group?: 'standard' | 'custom';
};

export interface SearchableDropdownProps {
  selectedId: string | null;
  onChange: (option: SearchableDropdownOption) => void;
  onCustomSelect?: (tag: string) => void;
  buttonVariant?: SlButtonVariant;
  defaultLabel?: string | React.ReactNode;
  placeholder?: string;
  /** If provided, override standard options; otherwise we infer from entity context */
  standardOptions?: SearchableDropdownOption[];
  /** Metric/field info for custom Doris fetch */
  customMetricType?: string; // default: 'ProductAttributes'
  customFieldName?: string; // default: 'attributeName'
  /** Controls whether to load custom (Product Attributes) section */
  enableCustom?: boolean;
  /** When custom is selected upstream, pass the selected tag so we can surface it first in CUSTOM. */
  selectedCustomTag?: string;
}

/**
 * Searchable dropdown with grouped sections (STANDARD, CUSTOM).
 * Styled to match SlDropdownMenu: title button, menu paddings, and section typography.
 */
const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  selectedId,
  onChange,
  onCustomSelect,
  buttonVariant = 'title',
  defaultLabel = '',
  placeholder = 'Search criteria',
  standardOptions,
  customMetricType = 'ProductAttributes',
  customFieldName = 'attributeName',
  enableCustom = true,
  selectedCustomTag
}) => {
  const theme = useStacklineTheme();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const inferredStandardOptions = useDorisEntityDropdownOptions(DorisDropdownFieldNameEnum.Sales);
  const computedStandardOptions = useMemo<SearchableDropdownOption[]>(() => {
    const base = standardOptions || inferredStandardOptions;
    return base.map((opt) => ({ ...opt, group: 'standard' as const }));
  }, [inferredStandardOptions, standardOptions]);

  // Doris CUSTOM options
  const { data: customData, isLoading: isCustomLoading } = useDorisData<{
    metricType: string;
    fieldName?: string;
    records: { [key: string]: string }[];
  }>({
    methodName: 'sales/GetSalesMetrics',
    metricType: customMetricType,
    fieldName: undefined,
    resultCount: 500,
    enabled: enableCustom,
    skipComparison: true,
    additionalParams: {
      interceptBaseParams: (payload: Record<string, any>) => {
        const { startWeekId, endWeekId, ...rest } = payload;
        return rest;
      }
    }
  });

  const customOptions = useMemo<SearchableDropdownOption[]>(() => {
    if (!enableCustom || !customData?.records) return [];
    const seen = new Set<string>();
    const mapped: SearchableDropdownOption[] = [];
    for (const row of customData.records) {
      const key = customFieldName;
      const raw = row[key as keyof typeof row] as unknown as string;
      if (!raw || typeof raw !== 'string') continue;
      if (seen.has(raw)) continue;
      seen.add(raw);
      mapped.push({ id: raw, label: raw, group: 'custom' });
    }
    return mapped;
  }, [customData, customFieldName, enableCustom]);

  const allOptions = useMemo(() => {
    const lower = search.trim().toLowerCase();
    const filterFn = (o: SearchableDropdownOption) =>
      !lower || o.label.toLowerCase().includes(lower) || o.id.toLowerCase().includes(lower);

    let std = computedStandardOptions.filter(filterFn);
    let cus = customOptions.filter(filterFn);

    // Reorder: move selected to top of its section
    if (selectedId) {
      std = std.sort((a, b) => (a.id === selectedId ? -1 : b.id === selectedId ? 1 : 0));
    }
    if (selectedId === 'productAttributeValue' && selectedCustomTag) {
      cus = cus.sort((a, b) => (a.id === selectedCustomTag ? -1 : b.id === selectedCustomTag ? 1 : 0));
    }

    return { std, cus };
  }, [computedStandardOptions, customOptions, search, selectedCustomTag, selectedId]);

  const hasStd = allOptions.std.length > 0;
  const hasCus = enableCustom && allOptions.cus.length > 0;
  const showEmptyState = !isCustomLoading && !hasStd && !hasCus;

  const selectedOptionLabel = useMemo(() => {
    if (selectedId === 'productAttributeValue' && selectedCustomTag) {
      return titleCase(selectedCustomTag);
    }
    const match = [...computedStandardOptions, ...customOptions].find((o) => o.id === selectedId);
    return match ? match.label : defaultLabel;
  }, [computedStandardOptions, customOptions, defaultLabel, selectedCustomTag, selectedId]);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (option: SearchableDropdownOption) => {
    if (option.group === 'custom' && onCustomSelect) {
      onCustomSelect(option.id);
      handleClose();
      return;
    }
    onChange(option);
    handleClose();
  };

  const renderHighlighted = (label: string) => {
    const query = search.trim();
    if (!query) return label;
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig');
    const parts = label.split(regex);
    return (
      <span>
        {parts.map((part, idx) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <span key={idx} style={{ fontWeight: 700 }}>
              {part}
            </span>
          ) : (
            <span key={idx}>{part}</span>
          )
        )}
      </span>
    );
  };

  // Keep focus in the input while filtering
  useEffect(() => {
    if (!anchorEl) return;
    if (searchInputRef.current) {
      const el = searchInputRef.current as HTMLInputElement;
      if (document.activeElement !== el) el.focus();
    }
  }, [search, anchorEl]);

  return (
    <>
      <SlButton
        onClick={handleOpen}
        variant={buttonVariant}
        endIcon={
          buttonVariant === 'title' ? (
            <DynamicDownChevron strokeWidth="2px" color={theme.colors.primary} style={{ marginTop: '-2px' }} />
          ) : undefined
        }
        buttonSx={buttonVariant === 'title' ? { padding: 0 } : undefined}
      >
        {selectedOptionLabel}
      </SlButton>

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        sx={{
          '& ul': { paddingY: 0 },
          '& .MuiPaper-root': {
            marginTop: '8px',
            width: 'fit-content',
            minWidth: '144px',
            maxWidth: '244px',
            maxHeight: '268px',
            overflowY: 'auto',

            scrollbarWidth: 'thin',
            scrollbarColor: `${hexToRgba(theme.colors.primaryGray, 0.8)} transparent`,
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: hexToRgba(theme.colors.primaryGray, 0.8),
              borderRadius: '8px'
            },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' }
          }
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ padding: '8px 12px 8px 12px' }}>
          <SearchTextField
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
            inputRef={searchInputRef}
            autoFocus
            startAdornment={
              <AppImage src="/svg/icons/search.svg" alt="search-icon" style={{ height: 16, width: 16 }} />
            }
            sx={{
              width: 'auto',
              border: 'none',
              boxShadow: 'none',
              padding: 0,
              backgroundColor: 'transparent',
              '& div.MuiInput-root': {
                width: 'auto',
                height: '20px',
                alignItems: 'center',
                padding: 0,
                '& input': {
                  color: theme.colors.primary,
                  marginLeft: '8px',
                  padding: 0,
                  height: '20px',
                  lineHeight: '20px'
                }
              }
            }}
            onKeyDown={(e) => {
              const key = e.key;
              const isPrintable = key.length === 1 || key === 'Backspace' || key === 'Delete';
              if (isPrintable) e.stopPropagation();
            }}
          />
        </Box>

        {hasStd ? (
          <>
            <Box
              sx={{
                padding: '4px 12px',
                backgroundColor: hexToRgba(theme.colors.secondaryPorcelain, 1),
                borderBottom: `1px solid ${theme.colors.primaryGray}`,
                height: '24px'
              }}
            >
              <Text variant="h6" sx={optionTitleTextStyle}>
                STANDARD
              </Text>
            </Box>
            {allOptions.std.map((opt, idx) => (
              <MenuItem
                key={`std-${opt.id}-${idx}`}
                onClick={() => handleSelect(opt)}
                selected={selectedId === opt.id}
                sx={{
                  '&:hover': { backgroundColor: hexToRgba(theme.colors.secondaryPorcelain, 1) },
                  backgroundColor: selectedId === opt.id ? hexToRgba(theme.colors.primaryLight, 0.3) : undefined,
                  borderBottom: idx === allOptions.std.length - 1 ? 'none' : `1px solid ${theme.colors.primaryGray}`,
                  width: '100%'
                }}
              >
                <Text variant="body2" sx={optionMenuItemTextStyle}>
                  {renderHighlighted(opt.label)}
                </Text>
              </MenuItem>
            ))}
          </>
        ) : null}

        {enableCustom && (isCustomLoading || hasCus) ? (
          <>
            <Box
              sx={{
                padding: '4px 12px',
                backgroundColor: hexToRgba(theme.colors.secondaryPorcelain, 1),
                borderTop: `1px solid ${theme.colors.primaryGray}`,
                borderBottom: `1px solid ${theme.colors.primaryGray}`,
                height: '24px'
              }}
            >
              <Text variant="h6" sx={optionTitleTextStyle}>
                CUSTOM
              </Text>
            </Box>
            {isCustomLoading ? (
              <MenuItem disabled>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Box
                    component="span"
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      border: `2px solid ${theme.colors.primaryGray}`,
                      borderTopColor: theme.colors.primary,
                      animation: 'spin 1s linear infinite'
                    }}
                  />
                  <Text variant="subtitle2">Loading...</Text>
                </Box>
              </MenuItem>
            ) : (
              allOptions.cus.map((opt, idx) => (
                <MenuItem
                  key={`cus-${opt.id}-${idx}`}
                  onClick={() => handleSelect(opt)}
                  selected={
                    selectedId === opt.id || (selectedId === 'productAttributeValue' && selectedCustomTag === opt.id)
                  }
                  sx={{
                    '&:hover': { backgroundColor: hexToRgba(theme.colors.secondaryPorcelain, 1) },
                    backgroundColor:
                      selectedId === opt.id || (selectedId === 'productAttributeValue' && selectedCustomTag === opt.id)
                        ? hexToRgba(theme.colors.primaryLight, 0.3)
                        : undefined,
                    borderBottom: idx === allOptions.cus.length - 1 ? 'none' : `1px solid ${theme.colors.primaryGray}`,
                    width: '100%'
                  }}
                >
                  <Text variant="body2" sx={optionMenuItemTextStyle}>
                    {renderHighlighted(titleCase(opt.label))}
                  </Text>
                </MenuItem>
              ))
            )}
          </>
        ) : null}

        {showEmptyState ? (
          <MenuItem disabled>
            <Text variant="subtitle2" color="secondary">
              No search results found
            </Text>
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
};

export default SearchableDropdown;
